import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { get } from '../../utils/api';
import { getKotilingaCategorySlugsCsv } from '../../utils/kotilingaScope';

function formatAmount(n) {
  const v = Number(n);
  if (!isFinite(v)) return '--';
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Orders Dialog (for completed orders by product)
export function OrdersDialog({ open, onClose, product, buildProdRangeParams, formatAmount, useParentCategorySlugs }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [aggregated, setAggregated] = React.useState([]);
  const [totals, setTotals] = React.useState({ total_amount: 0, count: 0 });
  const [expandCustomer, setExpandCustomer] = React.useState({});

  React.useEffect(() => {
    if (!open || !product) {
      setRows([]);
      setAggregated([]);
      setTotals({ total_amount: 0, count: 0 });
      setExpandCustomer({});
      return;
    }

    const loadOrders = async () => {
      try {
        setLoading(true);
        setError('');
        const prodRange = buildProdRangeParams();
        const productId = product.product_id;
        const scopeSlugs = getKotilingaCategorySlugsCsv();
        const categoryParam = useParentCategorySlugs
          ? `parent_category_slugs=${encodeURIComponent(useParentCategorySlugs)}`
          : `exclude_parent_category_slugs=${encodeURIComponent(scopeSlugs)}`;
        
        let allCompletedRows = [];
        let currentPage = 1;
        const perPage = 1000;
        let hasMore = true;
        
        while (hasMore) {
          const res = await get(`/ldb/orders/search?product_id=${encodeURIComponent(productId)}&page=${currentPage}&per_page=${perPage}&${categoryParam}${prodRange}`);
          const batch = Array.isArray(res?.data) ? res.data : [];
          
          const totalCount = res?.meta?.total || 0;
          const totalPages = Math.ceil(totalCount / perPage);
          
          const hasProduct = (r) => {
            const li = r.items || r.line_items || [];
            return li.some(it => String(it.product_id ?? it.id ?? it.productId) === String(productId));
          };
          const filtered = batch.filter(hasProduct);
          const completedRows = filtered.filter(r => String(r.status || '').toLowerCase() === 'completed');
          
          allCompletedRows = allCompletedRows.concat(completedRows);
          
          hasMore = (batch.length === perPage || currentPage < totalPages) && currentPage < 100;
          if (hasMore) {
            currentPage++;
          }
        }
        
        const customerMap = new Map();
        allCompletedRows.forEach(r => {
          const email = String(r.billing_email || '').trim().toLowerCase();
          let key;
          if (email && email !== '') {
            key = email;
          } else if (r.customer_id) {
            key = `cust#${r.customer_id}`;
          } else {
            key = `order#${r.id}`;
          }
          
          const items = r.items || r.line_items || [];
          const productItems = items.filter(it => String(it.product_id ?? it.id ?? it.productId) === String(productId));
          const productAmount = productItems.reduce((sum, item) => {
            return sum + Number(item.total_num || item.subtotal_num || item.price_num * item.quantity || 0);
          }, 0);
          
          const existing = customerMap.get(key) || {
            customer_id: r.customer_id,
            name: `${r.billing_first_name || ''} ${r.billing_last_name || ''}`.trim() || 'Unknown',
            email: r.billing_email || '',
            phone: r.billing_phone || '',
            payments_count: 0,
            total_amount: 0,
            orders: []
          };
          
          existing.payments_count += 1;
          existing.total_amount += productAmount;
          existing.orders.push({ ...r, product_amount: productAmount });
          
          customerMap.set(key, existing);
        });
        
        const aggregatedData = Array.from(customerMap.values())
          .sort((a, b) => b.total_amount - a.total_amount)
          .map((item, idx) => ({ ...item, serial: idx + 1 }));
        
        const sum = aggregatedData.reduce((acc, r) => acc + r.total_amount, 0);
        
        setRows(allCompletedRows);
        setAggregated(aggregatedData);
        setTotals({ total_amount: sum, count: allCompletedRows.length });
      } catch (e) {
        setError(e?.message || 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [open, product, buildProdRangeParams]);

  const toggleCustomerDetails = (customerKey) => {
    setExpandCustomer(prev => ({
      ...prev,
      [customerKey]: !prev[customerKey]
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        {product ? (product.product_name || `#${product.product_id}`) : 'Orders'} — Orders
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'error.light', color: 'common.white', '&:hover': { bgcolor: 'error.main' }, borderRadius: '50%' }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2 }}>Loading...</Typography>
          </Box>
        )}

        {!loading && (
          <>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '60px 1fr 100px 120px 120px', bgcolor: 'action.hover' }}>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}>S/N</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}>Devotee Name</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>No. Payments</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Total Amount</Typography>
                <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'center' }}>Details</Typography>
              </Box>
              <Box>
                {aggregated.map((customer, idx) => {
                  const customerKey = customer.email || `customer_${customer.customer_id}`;
                  const isExpanded = expandCustomer[customerKey];
                  return (
                    <React.Fragment key={customerKey}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '60px 1fr 100px 120px 120px', borderTop: '1px solid', borderColor: 'divider', bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover' }}>
                        <Typography variant="body2" sx={{ p: 1 }}>{customer.serial || idx + 1}</Typography>
                        <Typography variant="body2" sx={{ p: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={customer.name}>{customer.name || 'Unknown'}</Typography>
                        <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{customer.payments_count || 0}</Typography>
                        <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(customer.total_amount)}</Typography>
                        <Box sx={{ p: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => toggleCustomerDetails(customerKey)}
                            sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                          >
                            {isExpanded ? 'Hide' : 'Details'}
                          </Button>
                        </Box>
                      </Box>
                      {isExpanded && customer.orders && customer.orders.length > 0 && (
                        <Box sx={{ bgcolor: 'action.selected', borderTop: '1px dashed', borderColor: 'divider' }}>
                          <Box sx={{ p: 2, pl: 4 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block' }}>Individual Transactions:</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '80px 140px 140px', columnGap: 2, rowGap: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>Order #</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>Transaction Date</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>Amount</Typography>
                              {customer.orders.map((order) => (
                                <React.Fragment key={order.id}>
                                  <Typography variant="body2">{order.order_number || `#${order.id}`}</Typography>
                                  <Typography variant="body2">
                                    {(() => {
                                      try {
                                        const d = new Date(order.date_created_gmt || order.date_created);
                                        return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
                                      } catch {
                                        return order.date_created_gmt || order.date_created;
                                      }
                                    })()}
                                  </Typography>
                                  <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatAmount(order.product_amount || order.total_num || order.total || 0)}</Typography>
                                </React.Fragment>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
                {aggregated.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No completed payments found</Typography>
                )}
              </Box>
            </Box>

            {aggregated.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total: {aggregated.length} customers, {totals.count} payments, {formatAmount(totals.total_amount)}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

